import { useForm } from 'react-hook-form'
import { AxiosError } from 'axios'
import Add from '@mui/icons-material/Add'
import Remove from '@mui/icons-material/Remove'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  Stack,
  Typography,
} from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import { useFeatureFlagContext } from '@/feature_flags/FeatureFlagProvider'
import usePolicyPositionOptions from '@/hooks/usePolicyPositionOptions'
import {
  useCreateApprovalPolicy,
  useUpdateApprovalPolicy,
} from '@/service-library/hooks/approval-policies'
import {
  ApprovalOrder,
  ApprovalPolicy,
  ApprovalPolicyApprover,
} from '@/types/approvals'
import {
  approverRefKey,
  orderedApproverRefs,
  toApproverEntries,
} from '@/utils/approver-entries'
import { showErrorSnackbar, showSuccessSnackbar } from '@/utils/snackbars'
import generateUuid from '@/utils/uuid'
import ApproverList from '@/components/approvals/ApproverList'
import { FormTextField, PixyDocsForm } from '@/components/forms'
import FormRadioGroup from '@/components/forms/FormRadioGroup'
import {
  draftPillDotSx,
  draftPillSx,
  unsavedCardBorderColor,
} from '../project-settings/line-item-rollup/rollupWarningStyles'
import { useProjectContext } from '../project-tables/ProjectProvider'

type ApprovalPolicyEditorProps = {
  /** Omit to create a new policy. */
  policy?: ApprovalPolicy
  listQueryKey: QueryKey
  onClose: () => void
}

/**
 * These fields match what save sends, not the controls on screen, so
 * `isDirty` compares against what would actually be saved.
 * `approvals_required` is null when every approver is required — the "All"
 * checkbox — and null is the only value Sequential can have.
 */
type ApprovalPolicyFormValues = {
  description: string
  llm_trigger_prompt: string
  approval_order: ApprovalOrder
  approvals_required: number | null
  approvers: ApprovalPolicyApprover[]
}

function getErrorDetail(error: unknown): string | undefined {
  const data = (error as AxiosError)?.response?.data
  if (!data || typeof data !== 'object') return undefined
  const detail = (data as Record<string, unknown>).detail
  return typeof detail === 'string' ? detail : undefined
}

type ApprovalOrderOptionProps = {
  value: ApprovalOrder
  title: string
  description: string
  selected: boolean
}

function ApprovalOrderOption({
  value,
  title,
  description,
  selected,
}: ApprovalOrderOptionProps) {
  return (
    <Paper
      variant="outlined"
      sx={{
        flex: 1,
        borderColor: selected ? 'primary.main' : 'divider',
        borderWidth: selected ? 2 : 1,
      }}
    >
      <FormControlLabel
        value={value}
        control={<Radio />}
        sx={{ m: 0, width: '100%', alignItems: 'flex-start', px: 1.5, py: 1 }}
        label={
          <Stack spacing={0.25} sx={{ pt: 0.75 }}>
            <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Stack>
        }
      />
    </Paper>
  )
}

export default function ApprovalPolicyEditor({
  policy,
  listQueryKey,
  onClose,
}: ApprovalPolicyEditorProps) {
  const { project } = useProjectContext()
  const featureFlags = useFeatureFlagContext()

  // A policy that already names a position keeps resolving it even with the
  // flag off — the flag only stops new positions from being added. Without
  // this, an existing position approver would render as "Unknown position",
  // making the policy look like it needs fewer approvals than it does.
  const namesPosition = !!policy?.approvers.some(
    ({ kind }) => kind === 'position',
  )

  const { positionOptions, isPending: positionsPending } =
    usePolicyPositionOptions({
      enabled: featureFlags.positions || namesPosition,
    })

  const methods = useForm<ApprovalPolicyFormValues>({
    defaultValues: {
      description: policy?.description ?? '',
      llm_trigger_prompt: policy?.llm_trigger_prompt ?? '',
      approval_order: policy?.approval_order ?? 'sequential',
      approvals_required: policy?.approvals_required ?? null,
      // Renumber up front: the list control always emits 0, 1, 2…, so a
      // saved policy with gaps would look edited forever once touched.
      approvers: policy
        ? toApproverEntries(orderedApproverRefs(policy.approvers))
        : [],
    },
  })
  const { formState, setValue, watch } = methods
  const { isDirty, isValid } = formState

  const values = watch()
  const approvalOrder = values.approval_order
  const approvalsRequired = values.approvals_required
  const approvers = values.approvers
  const isAllRequired = approvalsRequired === null

  const { createApprovalPolicy, isPending: isCreating } =
    useCreateApprovalPolicy({
      listQueryKey,
      onSuccess: () => {
        showSuccessSnackbar('Approval Policy Created')
        onClose()
      },
      onError: (error) => {
        showErrorSnackbar(
          getErrorDetail(error) ||
            'Failed to create approval policy. Please try again later.',
        )
      },
    })

  const { updateApprovalPolicy, isPending: isUpdating } =
    useUpdateApprovalPolicy({
      listQueryKey,
      onSuccess: () => {
        showSuccessSnackbar('Approval Policy Updated')
        onClose()
      },
      onError: (error) => {
        showErrorSnackbar(
          getErrorDetail(error) ||
            'Failed to update approval policy. Please try again later.',
        )
      },
    })

  const hasPositionApprover = approvers.some(({ kind }) => kind === 'position')

  const isSaving = isCreating || isUpdating
  // The approver list isn't a registered field, so isValid can't cover it.
  const canSave = isValid && isDirty && approvers.length > 0

  // Changing any of these makes the server version the policy on save.
  // `description` is only a name admins read, so changing it does not.
  const willCreateNewVersion =
    !!policy &&
    (values.llm_trigger_prompt.trim() !== policy.llm_trigger_prompt ||
      approvalOrder !== policy.approval_order ||
      approvalsRequired !== policy.approvals_required ||
      orderedApproverRefs(approvers).map(approverRefKey).join(',') !==
        orderedApproverRefs(policy.approvers).map(approverRefKey).join(','))

  // A new policy has never been saved, which outranks merely-dirty.
  const draftLabel = !policy
    ? 'Draft — never saved'
    : isDirty
    ? 'Unsaved changes'
    : null

  const handleSave = (formValues: ApprovalPolicyFormValues) => {
    // handleSubmit only blocks on the text fields' rules, so Enter could
    // still save an empty approver list or an unchanged policy.
    if (!isDirty || approvers.length === 0) return

    const payload = {
      ...formValues,
      description: formValues.description.trim(),
      llm_trigger_prompt: formValues.llm_trigger_prompt.trim(),
      // A saved sequential policy can come back with a stale count, and
      // nothing on screen clears it.
      approvals_required:
        formValues.approval_order === 'sequential'
          ? null
          : formValues.approvals_required,
    }

    if (policy) {
      updateApprovalPolicy({ ...policy, ...payload })
    } else {
      createApprovalPolicy({
        id: generateUuid(),
        project_id: project.id,
        is_enabled: true,
        ...payload,
      })
    }
  }

  const setApprovalsRequired = (next: number | null) => {
    setValue('approvals_required', next, { shouldDirty: true })
  }

  const handleApprovalOrderChange = (order: string) => {
    // Sequential means every approver signs off, so a count has no meaning.
    if (order === 'sequential') setApprovalsRequired(null)
  }

  const handleApproversChange = (next: ApprovalPolicyApprover[]) => {
    setValue('approvers', next, { shouldDirty: true })
    // Removing an approver can leave the count above the new list length.
    if (approvalsRequired === null || next.length === 0) return
    const clamped = Math.min(Math.max(approvalsRequired, 1), next.length)
    if (clamped !== approvalsRequired) setApprovalsRequired(clamped)
  }

  return (
    <Card
      sx={{
        p: 3,
        border: (theme) =>
          draftLabel ? `1px solid ${unsavedCardBorderColor(theme)}` : undefined,
      }}
    >
      <PixyDocsForm
        id="approval-policy-form"
        methods={methods}
        onSubmit={handleSave}
      >
        <Stack spacing={3}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="h6">
              {policy ? 'Edit approval policy' : 'New approval policy'}
            </Typography>
            {draftLabel && (
              <Chip
                size="small"
                label={draftLabel}
                icon={<Box sx={draftPillDotSx} />}
                sx={draftPillSx}
              />
            )}
          </Stack>

          <FormTextField
            name="description"
            label="Policy name"
            placeholder="e.g. High-value invoices"
            fullWidth
            required
            useControllerProps={{
              rules: { validate: (value) => value.trim().length > 0 },
            }}
          />

          <FormTextField
            name="llm_trigger_prompt"
            label="Conditions"
            placeholder='e.g. "The invoice total is over $10,000."'
            helperText="Describe the conditions for when this policy should apply to a document in natural language."
            multiline
            minRows={3}
            fullWidth
            required
            useControllerProps={{
              rules: { validate: (value) => value.trim().length > 0 },
            }}
          />

          <Stack spacing={1}>
            <Typography variant="subtitle2">Approval order</Typography>
            <FormRadioGroup
              name="approval_order"
              fullWidth
              radioGroupProps={{
                'aria-label': 'Approval order',
                onChange: (_event, order) => handleApprovalOrderChange(order),
              }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <ApprovalOrderOption
                  value="sequential"
                  title="Sequential"
                  description="Approvers sign off one at a time, in the order below. All of them are required."
                  selected={approvalOrder === 'sequential'}
                />
                <ApprovalOrderOption
                  value="any"
                  title="Any order"
                  description="Approvers can sign off in any order. The policy is satisfied once the required number have approved."
                  selected={approvalOrder === 'any'}
                />
              </Stack>
            </FormRadioGroup>
          </Stack>

          <Stack spacing={1}>
            {/* The browser submits a form when Enter is pressed in a text
                input, so Enter in the approver search would save the policy.
                preventDefault stops that; stopping propagation would not.
                Inputs only — Enter must still click the list's buttons. */}
            <Box
              onKeyDown={(event) => {
                if (
                  event.key === 'Enter' &&
                  (event.target as HTMLElement).tagName === 'INPUT'
                ) {
                  event.preventDefault()
                }
              }}
            >
              <ApproverList
                value={approvers}
                onChange={handleApproversChange}
                positionOptions={positionOptions}
                canAddPositions={featureFlags.positions}
                positionsPending={positionsPending}
                showNumbers={approvalOrder === 'sequential'}
              />
            </Box>

            {hasPositionApprover && (
              <Typography variant="caption" color="text.secondary">
                A position is satisfied once everyone who currently holds it has
                approved, so the number of approvals required counts entries in
                this list, not people.
              </Typography>
            )}

            {approvalOrder === 'any' && approvers.length > 0 && (
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ pt: 0.5 }}
              >
                <Typography variant="body2">Approvals required</Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isAllRequired}
                      onChange={(event) => {
                        // Unchecking starts from what "All" already meant, so
                        // the count doesn't jump down to 1 on the way out.
                        setApprovalsRequired(
                          event.target.checked
                            ? null
                            : Math.max(approvers.length, 1),
                        )
                      }}
                    />
                  }
                  label="All"
                />
                {!isAllRequired && (
                  <>
                    <Stack direction="row" spacing={0} alignItems="center">
                      <IconButton
                        size="small"
                        aria-label="Decrease approvals required"
                        disabled={approvalsRequired <= 1}
                        onClick={() =>
                          setApprovalsRequired(
                            Math.max(1, approvalsRequired - 1),
                          )
                        }
                      >
                        <Remove fontSize="small" />
                      </IconButton>
                      <Typography
                        data-testid="approvals-required-count"
                        sx={{
                          minWidth: 20,
                          textAlign: 'center',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {approvalsRequired}
                      </Typography>
                      <IconButton
                        size="small"
                        aria-label="Increase approvals required"
                        disabled={approvalsRequired >= approvers.length}
                        onClick={() =>
                          setApprovalsRequired(
                            Math.min(approvers.length, approvalsRequired + 1),
                          )
                        }
                      >
                        <Add fontSize="small" />
                      </IconButton>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      of {approvers.length} approvers
                    </Typography>
                  </>
                )}
              </Stack>
            )}
          </Stack>

          <Divider />

          {willCreateNewVersion && (
            <Typography variant="caption" color="text.secondary">
              Saving creates a new version of this policy. Documents nobody has
              approved or rejected yet pick it up immediately; documents with
              approvals already recorded stay on their current version until an
              admin updates them.
            </Typography>
          )}

          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button variant="text" onClick={onClose}>
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={isSaving}
              disabled={!canSave}
            >
              Save Policy
            </LoadingButton>
          </Stack>
        </Stack>
      </PixyDocsForm>
    </Card>
  )
}
